import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import testimonialsData from "../data/testimonials.json"
import { SOCIAL_SCHEMA_SAME_AS } from "../utils/socialProfiles"

const Seo = ({
  title,
  description,
  image,
  pathname = "/",
  robots = "index, follow",
  schemaMarkup,
  children,
}) => {
  const {
    site: { siteMetadata },
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
        }
      }
    }
  `)

  const coerceRating = val => {
    if (typeof val === "number" && !isNaN(val)) return val
    if (typeof val === "string") {
      // try digit first
      const digits = val.match(/\d+(\.\d+)?/)
      if (digits) return Number(digits[0])
      // then count stars
      const stars = (val.match(/⭐/g) || []).length
      if (stars) return stars
    }
    return 5 // default
  }

  // Ensure no trailing slash on siteUrl
  const rawSiteUrl = siteMetadata.siteUrl || ""
  const siteUrl = rawSiteUrl.endsWith("/")
    ? rawSiteUrl.slice(0, -1)
    : rawSiteUrl

  const defaultTitle = siteMetadata.title
  const defaultDescription = siteMetadata.description
  const defaultImage = "/images/og-zencarbuying.jpg"

  const metaTitle = title || defaultTitle
  const metaDescription = description || defaultDescription

  const metaImage =
    image?.startsWith("http") || image?.startsWith("//")
      ? image
      : `${siteUrl}${image || defaultImage}`

  // Canonical with trailing slash normalization
  const canonicalPath =
    pathname === "/" ? "/" : pathname.endsWith("/") ? pathname : `${pathname}/`
  const url = `${siteUrl}${canonicalPath}`

  const reviewsForSchema = testimonialsData.slice(0, 10)

  const avgRating = reviewsForSchema.length
    ? (
        reviewsForSchema.reduce((sum, t) => sum + coerceRating(t.rating), 0) /
        reviewsForSchema.length
      ).toFixed(1)
    : "5.0"

  const showReviews =
    canonicalPath === "/" ||
    canonicalPath === "/services/" ||
    canonicalPath === "/reviews/"

  const orgId = `${siteUrl}/#organization`

  const globalSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": orgId,
    name: "Zen Car Buying",
    description: defaultDescription,
    url: siteUrl,
    logo: `${siteUrl}/zen-car-buying-logo.png`,
    image: metaImage,
    priceRange: "$$",
    areaServed: "US",
    availableChannel: "Online",
    serviceType: "Car Buying Concierge",
    telephone: "+1-555-555-0100",
    sameAs: SOCIAL_SCHEMA_SAME_AS,
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+1-555-555-0100",
        contactType: "Customer Support",
        email: "info@zencarbuying.com",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
    ...(showReviews && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: String(avgRating),
        reviewCount: String(reviewsForSchema.length),
        bestRating: "5",
        worstRating: "1",
      },
      review: reviewsForSchema.map((t, i) => ({
        "@type": "Review",
        "@id": `${siteUrl}/reviews/#review-${i + 1}`,
        reviewBody: t.quote,
        author: { "@type": "Person", name: t.name },
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(coerceRating(t.rating)),
          bestRating: "5",
          worstRating: "1",
        },
        itemReviewed: {
          "@type": "LocalBusiness",
          "@id": orgId,
        },
      })),
    }),
  }

  return (
    <>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots} />
      <meta property="og:site_name" content="Zen Car Buying" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={url} />
      <meta
        property="og:type"
        content={canonicalPath.startsWith("/blog/") ? "article" : "website"}
      />
      <meta property="og:image" content={metaImage} />
      <meta
        property="og:image:alt"
        content="Zen Car Buying | Stress-Free Nationwide Concierge Service for New, Lightly Used & Luxury Cars"
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:creator" content={siteMetadata.author} />

      <script type="application/ld+json">{JSON.stringify(globalSchema)}</script>

      {schemaMarkup && (
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      )}

      {children}
    </>
  )
}

export default Seo
